/**
 * Advanced column data type detection utility
 * Detects various data types including specialized formats like currency, email, dates, etc.
 * Supports nested object properties and sampling multiple rows for better type inference
 */

/**
 * Determines the data type of a column based on sampling multiple rows
 * @param {Object} column - The column definition with key and labelKey properties
 * @param {Array} rows - Array of data rows to analyze
 * @returns {String} The detected data type
 */
export const getColumnDataType = (column, rows) => {
  // First check if labelKey exists and use it, otherwise fall back to key
  const lookupKey =
    column.labelKey && column.labelKey !== "" ? column.labelKey : column.key;

  // If no key is provided (like for action columns), return 'action'
  if (!lookupKey || lookupKey.trim() === "") {
    return "action";
  }

  // Helper function to get nested values (e.g., "name.firstName")
  const getNestedValue = (obj, path) => {
    return path.split(".").reduce((current, key) => {
      return current && current[key] !== undefined ? current[key] : undefined;
    }, obj);
  };

  // Helper function to determine the data type of a single value
  const getValueDataType = (value) => {
    // Check for null/undefined first
    if (value === null) return "null";
    if (value === undefined) return "empty";
    if (value === "") return "empty";

    // Check for numbers
    if (typeof value === "number") {
      if (Number.isInteger(value)) return "integer";
      return "number";
    }

    // Check strings
    if (typeof value === "string") {
      const trimmedValue = value.trim();

      // Check for currency values
      if (trimmedValue.match(/^[\$€£¥₹]\s*[\d,]+\.?\d*$/)) return "currency";

      // Check for percentage values
      if (trimmedValue.match(/^\d+\.?\d*%$/)) return "percentage";

      // Check for numeric strings
      const numericValue = parseFloat(trimmedValue.replace(/,/g, ""));
      if (
        !isNaN(numericValue) &&
        isFinite(numericValue) &&
        trimmedValue.match(/^[\d,.-]+$/)
      ) {
        return Number.isInteger(numericValue) ? "integer" : "number";
      }

      // Check for date strings
      const date = new Date(trimmedValue);
      if (!isNaN(date.getTime())) {
        const datePatterns = [
          /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/, // ISO format
          /^\d{4}-\d{2}-\d{2}$/, // YYYY-MM-DD
          /^\d{2}\/\d{2}\/\d{4}$/, // MM/DD/YYYY
          /^\d{2}-\d{2}-\d{4}$/, // MM-DD-YYYY
        ];
        if (datePatterns.some((pattern) => pattern.test(trimmedValue))) {
          return "date";
        }
      }

      // Check for email
      if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedValue)) return "email";

      // Check for phone
      if (/^[\+]?[\d\s\-\(\)]{10,}$/.test(trimmedValue)) return "phone";

      // Check for URL
      try {
        new URL(trimmedValue);
        return "url";
      } catch {}

      // Check for boolean-like strings
      if (
        ["true", "false", "yes", "no", "1", "0"].includes(
          trimmedValue.toLowerCase()
        )
      ) {
        return "boolean";
      }

      return "string";
    }

    // Check for boolean
    if (typeof value === "boolean") return "boolean";

    // // Check for arrays
    // if (Array.isArray(value)) {
    //   if (value.length === 0) return "array";

    //   const firstItemType = getValueDataType(value[0]);
    //   const isHomogeneous = value.every(item => getValueDataType(item) === firstItemType);

    //   return isHomogeneous ? `array<${firstItemType}>` : "array<mixed>";
    // }

    // Check for arrays
    if (Array.isArray(value)) {
      return "array";
    }

    // Check for Date objects
    if (
      value instanceof Date ||
      (typeof value === "object" && value._bsontype === "Date") ||
      (typeof value === "object" &&
        value.constructor &&
        value.constructor.name === "Date")
    ) {
      return "date";
    }

    // Check for objects
    if (typeof value === "object") {
      return "object";
    }

    return "unknown";
  };

  // Sample multiple rows to get a better understanding of the data type
  const sampleSize = Math.min(10, rows.length);
  const sampleRows = rows.slice(0, sampleSize);
  const typeCounts = {};

  for (const row of sampleRows) {
    const value = getNestedValue(row, lookupKey);
    const type = getValueDataType(value);
    typeCounts[type] = (typeCounts[type] || 0) + 1;
  }

  // Return the most common type, but prioritize non-empty types
  const sortedTypes = Object.entries(typeCounts).sort(
    ([typeA, countA], [typeB, countB]) => {
      // Prioritize non-empty types
      if (typeA === "empty" && typeB !== "empty") return 1;
      if (typeA !== "empty" && typeB === "empty") return -1;
      // Then sort by count
      return countB - countA;
    }
  );

  return sortedTypes.length > 0 ? sortedTypes[0][0] : "unknown";
};

/**
 * Simplified version that works with a single row (for backward compatibility)
 * @param {Object} column - The column definition with key and labelKey properties
 * @param {Object} row - Single data row to analyze
 * @returns {String} The detected data type
 */
export const getColumnDataTypeFromSingleRow = (column, row) => {
  return getColumnDataType(column, [row]);
};

export default getColumnDataType;
